<template>
	<AppDialog :dialogName="'ModuleSettingsDialog'">
		<div class="pop-up-content-container display-flex flex-column">
			<div class="settings-dialog-title pb-1">Feature settings</div>

			<div class="settings-container">
				<div v-if="!isOfTypeSubFlow">
					<div class="setting-section-title first">User experience</div>

					<SettingSection title="Appearance">
						<template #description>
							Customize background, icon and progress bar for all interfaces. Only affects features when run in a URL.
						</template>
						<template #trigger>
							<div class="edit-button primary-link-style" @click="openFeatureAppearanceSettings">Edit</div>
						</template>
					</SettingSection>

					<SettingSection v-if="onboardingEnabled" title="Feature description">
						<template #description>Help users use this feature. Displayed when the user sees the feature for the first time.</template>
						<template #trigger>
							<div class="edit-button primary-link-style" @click="openOnboardingTextEditor">Edit</div>
						</template>
					</SettingSection>

					<SettingSection v-if="usePortalAuthEnabled && isPortal" title="Authentication">
						<template #description>Require users to authenticate in order to run this portal.</template>
						<template #trigger>
							<div class="edit-button primary-link-style" @click="triggerOpenPortalAuthSettings">Edit</div>
						</template>
					</SettingSection>
				</div>
				<div class="setting-section-title">Run-time</div>

				<SettingSection title="Intelligent A-Sync">
					<template #description>
						Turning this on results in faster performance by running steps in parallel (asynchronously). You can keep steps running in their
						place under “advanced” in step setup.
						<DocsDispatcher docsSrc="https://docs.ply.io/advanced-guides/intelligent-a-sync">
							<span class="link-text">Learn more</span>
						</DocsDispatcher>
					</template>
					<template #trigger>
						<div class="toggle-message mr-1" v-if="showPublishRequierdMessage">Changes will take affect after publishing</div>
						<AppSwitch class="swtch sml" v-model="asyncEnabled" />
					</template>
				</SettingSection>

				<SettingSection title="Setup" v-if="!isOfTypeSubFlow">
					<template #description> Creates a setup experience for users receiving this feature. </template>
					<template #trigger>
						<AppSwitch class="swtch sml" v-model="setupEnabled" :disabled="setupEnabledSwitchDisabled" />
					</template>
				</SettingSection>

				<SettingSection title="Secure all files uploaded in interfaces">
					<template #description>
						Generated file links will only be accessible to logged in workspace members who can use this feature
					</template>
					<template #trigger>
						<AppSwitch class="swtch sml" v-model="protectedFileUploads" />
					</template>
				</SettingSection>

				<div class="setting-section-title">Editing preferences</div>

				<SettingSection title="Friendly properties formatting">
					<template #description>
						Cleans property names in data mapper to make them more readable. Turn off to see original property names.
					</template>
					<template #trigger>
						<AppSwitch class="swtch sml" v-model="formattedPropertyName" />
					</template>
				</SettingSection>

				<SettingSection title="Data preview mode">
					<template #description> Choose how you want to see mapped data in the builder </template>
					<template #trigger>
						<div class="edit-button primary-link-style" @click="openPreviewSettings">Edit</div>
					</template>
				</SettingSection>

				<SettingSection title="Interface previews">
					<template #description>Show interface previews below the step name in the builder </template>
					<template #trigger>
						<AppSwitch class="swtch sml" v-model="interfacePreviewEnabled" />
					</template>
				</SettingSection>
				<SettingSection v-if="isNarrowBranchesLayoutFeatureFlagEnabled" title="Narrow branches layout">
					<template #description>If on, spaces between branches will be as narrow as possible, off will cause symmetric spacing</template>
					<template #trigger>
						<AppSwitch class="swtch sml" v-model="narrowBranchesLayout" />
					</template>
				</SettingSection>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SettingSection from './SettingSection';
import DocsDispatcher from '@/components/common/DocsDispatcher';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService, { openPortalAuthSettings } from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getService } from '@/store/modules/apps/getters';
import { user, shouldFormatPropertyKey } from '@/store/modules/auth/getters';
import {
	isNarrowBranchesLayoutEnabled as isNarrowBranchesLayoutFeatureFlagEnabled,
	isOnboardingEnabled,
	usePortalAuthEnabled,
} from '@/store/modules/featureFlags/getters';
import { updateWorkflow, setIsInterfacePreviewEnabled } from '@/store/modules/selectedWorkflow/actions';
import {
	editWorkflow,
	nodesData,
	isInterfacePreviewEnabled,
	isNarrowBranchesLayout,
	isOfTypeSubFlow,
	isPortal,
} from '@/store/modules/selectedWorkflow/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { useWorkflowsActions } from '@/store-composables/workflows/actions';
export default {
	name: 'ModuleSettingsDialog',
	components: {
		AppDialog,
		SettingSection,
		DocsDispatcher,
	},
	computed: {
		...mapGetters([
			user.fullPath,
			nodesData.fullPath,
			getService.fullPath,
			editWorkflow.fullPath,
			shouldFormatPropertyKey.fullPath,
			isOnboardingEnabled.fullPath,
			isInterfacePreviewEnabled.fullPath,
		]),
		...mapGettersShorthand({
			isNarrowBranchesLayout,
			isNarrowBranchesLayoutFeatureFlagEnabled,
			isOfTypeSubFlow,
			isPortal,
			usePortalAuthEnabled,
		}),
		asyncEnabled: {
			get() {
				return !!this.editWorkflow?.async_enabled;
			},
			set(value) {
				this.updateWorkflow({ data: { async_enabled: !!value } });
			},
		},
		setupEnabled: {
			get() {
				return !!this.editWorkflow?.setup_enabled;
			},
			set(value) {
				this.updateWorkflow({ data: { setup_enabled: !!value } });
			},
		},
		protectedFileUploads: {
			get() {
				return !!this.editWorkflow?.file_uploads_protected;
			},
			set(value) {
				this.updateWorkflow({ data: { file_uploads_protected: !!value } });
			},
		},
		narrowBranchesLayout: {
			get() {
				return this.isNarrowBranchesLayout;
			},
			set(value) {
				useWorkflowsActions().changeWorkflowUserSettings(this.editWorkflow.id, { is_narrow_branches_layout: !!value });
			},
		},
		setupEnabledSwitchDisabled() {
			return Boolean(
				this.editWorkflow.current_draft.general_config_options || this.editWorkflow.current_draft.user_specific_config_options
			);
		},
		formattedPropertyName: {
			get() {
				return this[shouldFormatPropertyKey.fullPath];
			},
			set(value) {
				this.saveUserMetadata({ format_property_names: value });
			},
		},
		interfacePreviewEnabled: {
			get() {
				return this[isInterfacePreviewEnabled.fullPath];
			},
			set(value) {
				this.setIsInterfacePreviewEnabled(value);
			},
		},
		editWorkflow() {
			return this[editWorkflow.fullPath];
		},
		showPublishRequierdMessage() {
			return this.asyncEnabledFirstState !== this.asyncEnabled;
		},
		onboardingEnabled() {
			return this[isOnboardingEnabled.fullPath];
		},
	},
	methods: {
		...mapActions(['saveUserMetadata']),
		...mapActionsShorthand({ updateWorkflow, setIsInterfacePreviewEnabled }),
		openPreviewSettings() {
			AppDialogManagerService.openPreviewSettingsDialog();
		},
		openFeatureAppearanceSettings() {
			AppDialogManagerService.openFeatureAppearanceSettingsDialog();
		},
		openOnboardingTextEditor() {
			AppDialogManagerService.openOnboardingTextEditorDialog();
		},
		triggerOpenPortalAuthSettings() {
			openPortalAuthSettings();
		},
	},
	created() {
		this.asyncEnabledFirstState = this.editWorkflow.async_enabled;
	},
	data() {
		return {
			asyncEnabledFirstState: false,
		};
	},
};
</script>

<style scoped lang="scss">
.pop-up-content-container {
	max-height: 90vh;
	min-width: 510px;
	padding: 25px 20px;
	box-shadow: 0px 3px 6px var(--shadow-color);

	.settings-dialog-title {
		font-size: 20px;
		font-weight: 600;
		color: var(--primary-font-color);
	}
	.setting-section-title:not(.first) {
		padding: 8px 0 0 0;
		border-top: 1px solid #e5e5e5;
	}
	.setting-section-title {
		margin: 20px 0 6px;
		font-weight: 700;
	}

	.link-text {
		cursor: pointer;
		font-size: 12px;
		text-decoration: underline;
		color: var(--primary-color);
	}

	.settings-container {
		overflow: auto;
	}
}
.edit-button {
	padding: 0 8px 0 0;
}

.toggle-message {
	position: absolute;
	bottom: 0;
	right: 0;
	font-size: 10px;
	color: var(--primary-error-font-color);
}
</style>
